import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Wrench, AlertTriangle, CheckCircle2, TrendingUp } from 'lucide-react'

interface VehicleHealthData {
  vehicle_id: string
  vehicle_name: string
  odometer_miles: number
  scheduled_maintenance_count: number
  overdue_maintenance_count: number
  last_service_date: number | null
  health_status: 'good' | 'needs_attention'
}

export function VehicleHealth() {
  const { data, isLoading } = useQuery({
    queryKey: ['vehicle-health'],
    queryFn: async () => {
      const res = await fetch('/api/maintenance/vehicle-health')
      if (!res.ok) throw new Error('Failed to fetch vehicle health')
      return res.json() as Promise<{ vehicles: VehicleHealthData[] }>
    },
    refetchInterval: 60000,
  })

  const totalVehicles = data?.vehicles.length || 0
  const needsAttention = data?.vehicles.filter(v => v.health_status === 'needs_attention').length || 0
  const healthy = totalVehicles - needsAttention

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="h-5 w-5" />
          Fleet Maintenance Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading vehicle health data...
          </div>
        ) : !data?.vehicles || data.vehicles.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No vehicle data available
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Vehicles</p>
                    <p className="text-2xl font-bold">{totalVehicles}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-muted-foreground" />
                </div>
              </div>

              <div className="rounded-lg border p-4 bg-green-500/10 border-green-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700 dark:text-green-400">Healthy</p>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-400">{healthy}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
              </div>

              <div className="rounded-lg border p-4 bg-orange-500/10 border-orange-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-700 dark:text-orange-400">Needs Attention</p>
                    <p className="text-2xl font-bold text-orange-700 dark:text-orange-400">{needsAttention}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </div>

            {/* Vehicle Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {data.vehicles.map((vehicle) => {
                const isHealthy = vehicle.health_status === 'good'
                const lastServiceDays = vehicle.last_service_date
                  ? Math.floor((Date.now() / 1000 - vehicle.last_service_date) / 86400)
                  : null

                return (
                  <Card key={vehicle.vehicle_id} className={`hover:shadow-md transition-shadow ${!isHealthy ? 'border-orange-500/30' : ''}`}>
                    <CardContent className="pt-6 space-y-3">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{vehicle.vehicle_name}</h3>
                          <p className="text-xs text-muted-foreground mt-1">
                            {vehicle.odometer_miles.toLocaleString()} miles
                          </p>
                        </div>
                        <Badge variant={isHealthy ? 'default' : 'destructive'}>
                          {isHealthy ? 'Good' : 'Attention'}
                        </Badge>
                      </div>

                      {/* Stats */}
                      <div className="space-y-2 pt-2 border-t">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Scheduled</span>
                          <span className="font-medium">{vehicle.scheduled_maintenance_count}</span>
                        </div>
                        {vehicle.overdue_maintenance_count > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-orange-600 dark:text-orange-400">Overdue</span>
                            <span className="font-medium text-orange-600 dark:text-orange-400">
                              {vehicle.overdue_maintenance_count}
                            </span>
                          </div>
                        )}
                        {lastServiceDays !== null && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Last Service</span>
                            <span className="text-xs">{lastServiceDays} days ago</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
